/**
 * The session context and its hook.
 *
 * Split from the provider so a component can read the session without importing the thing that
 * owns the query client subscription and the 401 handler — and so the provider stays the only
 * module in the app that can cause a redirect.
 */
import { createContext, useContext } from 'react';

import type { AuthUserDto } from '@catalog/shared';

export interface AuthContextValue {
  /** `null` means "definitely signed out", not "not known yet" — see `isPending`. */
  readonly user: AuthUserDto | null;
  readonly isPending: boolean;
  /** The session could not be determined: the service is unreachable, not signed out. */
  readonly error: unknown;
  readonly isAuthenticated: boolean;
  readonly isAdmin: boolean;
  /** Whether this deployment offers "Sign in with Endeavor X". */
  readonly ssoEnabled: boolean;
  readonly ssoLoginPath: string;
  /** Signs in with an email and password, then replaces the cached session. */
  readonly signIn: (email: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  /** Adopts a user the server has just created a session for, e.g. after accepting an invite. */
  readonly adoptSession: (user: AuthUserDto) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * @throws if called outside an `AuthProvider`. A silent `null` user would render the signed-out
 * UI to a signed-in person, which looks like a session bug and is much harder to find.
 */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth must be used within an <AuthProvider>.');

  return value;
}
